<script lang="ts">
  import { onMount } from 'svelte';
  import { getAppContext } from '../context.js';
  import { expertCampaignVersionMismatch } from '../../features/expert-review/campaign-contract.js';

  let feedback: typeof import('../../features/expert-review/feedback.js') | null = null;
  import type { SavedMatch } from '@texas42/app-model';
  const { toast, matchPersistence, repositories } = getAppContext();

  function sync(match?: SavedMatch): void {
    void feedback?.synchronizeExpertFeedback(match).catch((cause) => toast.show({
      title: 'Feedback is still on this device',
      description: cause instanceof Error ? cause.message : 'Could not prepare the upload.',
      variant: 'warning',
    }));
  }

  onMount(() => {
    const unsubscribe = matchPersistence.subscribe(sync);
    const interval = window.setInterval(() => { sync(); void feedback?.retryExpertRecordings(); }, 30_000);
    const arrivedWithInvite = new URLSearchParams(window.location.hash.slice(1)).has('expert');
    const api = import('../../features/expert-review/api.js');
    void api.then(({ initializeExpertSession }) => initializeExpertSession()).then(async (session) => {
      if (arrivedWithInvite && session.active && expertCampaignVersionMismatch(session)) {
        toast.show({
          title: 'Expert campaign needs another app version',
          description: 'Update or refresh the app before starting this private campaign.',
          variant: 'warning',
        });
      } else if (arrivedWithInvite && session.active) {
        toast.show({
          title: 'Expert review mode is active',
          description: `${session.reviewerLabel}, play as usual. Flag anything that looks wrong. Your notes and finished hands go to Josh.`,
          variant: 'success',
        });
      } else if (arrivedWithInvite && !session.active && session.reason) {
        toast.show({
          title: 'Expert invite unavailable',
          description: session.reason,
          variant: 'warning',
        });
      }
      if (session.active && !expertCampaignVersionMismatch(session)) {
        feedback = await import('../../features/expert-review/feedback.js');
        for (const match of await repositories.matches.list()) await feedback.synchronizeExpertFeedback(match);
        sync();
        void feedback?.retryExpertRecordings();
      }
    }).catch((cause) => {
      toast.show({
        title: 'Expert review storage unavailable',
        description: cause instanceof Error ? cause.message : 'The local expert queue could not open.',
        variant: 'warning',
      });
    });

    const handleOnline = () => {
      void api.then(async ({ flushExpertOutbox, initializeExpertSession }) => {
        const session = await initializeExpertSession({ force: true });
        if (session.active && !expertCampaignVersionMismatch(session)) {
          feedback ??= await import('../../features/expert-review/feedback.js');
          await flushExpertOutbox(session.campaignId, session.reviewerScope);
          sync();
          void feedback?.retryExpertRecordings();
        }
      }).catch((cause) => {
        toast.show({
          title: 'Expert review retry failed',
          description: cause instanceof Error ? cause.message : 'The local expert queue could not open.',
          variant: 'warning',
        });
      });
    };
    window.addEventListener('online', handleOnline);
    return () => { unsubscribe(); window.clearInterval(interval); window.removeEventListener('online', handleOnline); };
  });
</script>
